<script setup lang="ts">
import type { KjdsDetailPageProps } from '@/types/appTypes'

const props = withDefaults(defineProps<KjdsDetailPageProps>(), {})

const app = useNuxtApp()
const route = useRoute()
const kijidasConfig = useAppConfig().kijidas
const storeArticleList = useStoreArticleList()
const storeIndex = useStoreIndex()

const baseTitle = '美術館・博物館'
let headTitle = ''
let headDescription = ''

const nPastEventDay = 1095 // 過去イベント表示期間(3年)
let bPastEventExist = false // 過去イベント存在フラグ

const refData = reactive({
  eventNowCount: 2,
  eventFutureCount: 1,
  rating: 3,
  aTag: null, // タグリスト
  bPastEventDisp: false, // 過去イベント表示フラグ
  reportItems: [] as Article[],
  reportObjects: [] as ArticleObject[],
  presentItems: [] as Article[],
})

const setHead = () => {
  const articleTitle = props.kjdsDetailData.article.title
  headTitle = `${articleTitle} | 展覧会スケジュール・アクセス・料金`
  headDescription = `アイエム［インターネットミュージアム］による${articleTitle}の情報ページ。${articleTitle}の見どころ、開催される展覧会などを紹介しています。${articleTitle}のことならアイエムで！`
  if (props.kjdsDetailData.article.description?.length) {
    headDescription = props.kjdsDetailData.article.description
  }
  // ぱんくず作成
  if (route.query.fromname === 'foryou') {
    storeIndex.setBreadCrumbs([
      {
        text: 'For You',
        disabled: false,
        to: {
          name: 'mypage-foryou-tab',
          params: { tab: 'event' },
        },
      },
      {
        text: articleTitle,
        disabled: true,
      },
    ])
  } else {
    storeIndex.setBreadCrumbs([
      {
        text: baseTitle,
        disabled: false,
        to: { path: '/museum' },
      },
      {
        text: articleTitle,
        disabled: true,
      },
    ])
  }

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
    ogImage: props.kjdsDetailData.eyeCatch?.url ? props.kjdsDetailData.eyeCatch.url : undefined,
  })
  useHead(headInput)
}

// 過去イベント表示ボタン
const onPastEvent = () => {
  if (!refData.bPastEventDisp) {
    refData.bPastEventDisp = !refData.bPastEventDisp
  }
}

// イベントから表示カラム作成
if (!refData.aTag) {
  refData.aTag = app.$getArticleTags(props.kjdsDetailData.article)
} // タグリスト

// 過去イベント存在フラグ設定
if (app.$getArticleRelationsSingle(props.kjdsDetailData.article, 'event', true, nPastEventDay) != null) {
  bPastEventExist = true
}

onMounted(() => {
  storeArticleList.fetchList('reportRelation', {
    config: {
      query: {
        slug: {
          column: 'museum_article_slug',
          defaultValue: props.kjdsDetailData.article.article_slug,
        },
      },
    },
  })
    .then((res) => {
      const reportItems = res.data.articles as Article[]
      refData.reportItems = reportItems.filter(isVisibleMuseumRelatedReport)
      refData.reportObjects = res.data.article_objects
    })

  storeArticleList.fetchList('presentRelation', {
    config: {
      query: {
        slug: {
          column: 'museum_article_slug',
          defaultValue: props.kjdsDetailData.article.article_slug,
        },
      },
    },
  })
    .then((res) => {
      refData.presentItems = res.data.articles as Article[]
    })
})

setHead()

const isVisibleMuseumRelatedReport = (report: Article) => {
  const linkedEventSlug = report?.custom?.event_article_slug
  if (!linkedEventSlug) {
    // eventが紐づけられてないレポートは表示
    return true
  }

  const linkedEvent = props.kjdsDetailData.article.relations?.find(rel =>
    rel.article?.article_type_slug === 'event'
    && String(rel.article.article_slug) === String(linkedEventSlug),
  )?.article

  if (!linkedEvent) {
    // museum詳細のrelations内に該当eventが無い場合は非表示
    return false
  }

  const periodDiff = app.$getPeriodDiffDay(linkedEvent.term_from, linkedEvent.term_to)

  return periodDiff !== null
}
</script>

<template>
  <div class="p-articleDetailPage">
    <div>
      <MuseumBody
        v-bind:item="props.kjdsDetailData.article"
        v-bind:objects="props.kjdsDetailData.objects"
      />
    </div>

    <div class="c-link">
      <Clip
        class="c-link_clip"
        v-bind:mode="true"
        v-bind:dspcnt="true"
        v-bind:article-type-slug="props.kjdsDetailData.article.article_type_slug"
        v-bind:article-slug="props.kjdsDetailData.article.article_slug"
        v-bind:count="props.kjdsDetailData.article.user_favorite_count"
      />
      <ShareButtons
        v-bind:title="headTitle"
      />
    </div>

    <ArticleTagList
      v-if="refData.aTag.length > 0"
      v-bind:article-name="props.kjdsDetailData.article.article_type_slug"
      v-bind:items="refData.aTag"
      class="pc-only"
    />

    <RelationBox
      v-if="refData.presentItems.length > 0"
      article="present"
      v-bind:header="kjdsDetailData.article.title + ' のプレゼント'"
      v-bind:articles="refData.presentItems"
    />
    <RelationBox
      v-if="refData.reportItems.length > 0"
      article="report"
      v-bind:header="kjdsDetailData.article.title + ' のレポート'"
      v-bind:articles="refData.reportItems"
      v-bind:objects="refData.reportObjects"
    />
    <RelationBox
      article="event"
      v-bind:header="kjdsDetailData.article.title + ' の展覧会'"
      v-bind:relations="props.kjdsDetailData.article.relations"
      v-bind:past-event="false"
      v-bind:sort-type="'term_form'"
      v-bind:sort-new="false"
    />
    <RelationBox
      article="event"
      v-bind:header="kjdsDetailData.article.title + ' のイベント'"
      v-bind:relations="props.kjdsDetailData.article.relations"
      v-bind:past-event="false"
      event
    />
    <RelationBox
      article="special"
      v-bind:header="'関連する特集'"
      v-bind:relations="props.kjdsDetailData.article.relations"
    />
    <RelationBox
      article="gakugeiin"
      v-bind:header="'関連する求人・募集'"
      v-bind:relations="props.kjdsDetailData.article.relations"
    />

    <div
      v-if="!refData.bPastEventDisp && bPastEventExist"
      class="c-pass"
    >
      <div>
        <v-btn
          variant="outlined"
          size="large"
          class="c-pass_button IMCustomBtn"
          v-on:click="onPastEvent()"
        >
          <v-icon start>
            mdi-chevron-right
          </v-icon>
          このミュージアムに関連する過去の展覧会・イベント一覧を見る
        </v-btn>
      </div>
    </div>

    <template v-if="refData.bPastEventDisp">
      <RelationBox
        article="event"
        v-bind:header="'関連する過去の展覧会・イベント'"
        v-bind:relations="props.kjdsDetailData.article.relations"
        v-bind:past-event="true"
        v-bind:past-day="nPastEventDay"
        event
      />
    </template>

    <div class="c-pass">
      <div>
        <nuxt-link
          v-bind:to="'/museum'"
        >
          <v-btn
            variant="outlined"
            size="large"
            class="c-pass_button"
          >
            <v-icon start>
              mdi-chevron-right
            </v-icon>
            ミュージアム検索にもどる
          </v-btn>
        </nuxt-link>
      </div>
    </div>

    <ArticleTagList
      v-if="refData.aTag.length > 0"
      v-bind:article-name="props.kjdsDetailData.article.article_type_slug"
      v-bind:items="refData.aTag"
      class="sp-only"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-link {
  display: flex;
  justify-content: center;
  margin-top: 35px;

  @include responsive.mq_down(sp) {
    flex-direction: column;
    align-items: center;
  }

  > div {
    &:first-child {
      @include responsive.mq_down(sp) {
        margin: 0 0 20px;
      }
    }
  }
}

.c-pass {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 83px;
  margin-bottom: 50px;
  font-family: var(--g_font_family_NotoSansCJKjp);

  a {
    text-decoration: none;
  }

  @include responsive.mq_down(sp) {
    margin-top: 50px;
  }

  &_button {
    min-width: 400px !important;
    border: 2px solid var(--color_border_control);
    border-radius: 8px;
    font-family: var(--g_font_family_SawarabiGothic);
    font-size: 16px;
    font-weight: bold;
    white-space: nowrap;
    color: var(--g_color_text_black);

    @include responsive.mq_down(sp) {
      min-width: 0 !important;
      height: auto !important;
      padding: 14px 10px !important;
    }
  }
}
</style>
